/*!

=========================================================
* Paper Dashboard PRO React - v1.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Nav, Collapse } from 'reactstrap';
import styled from 'styled-components';
// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from 'perfect-scrollbar';
import classnames from 'classnames';
import smallIcon from 'assets/img/flare.png';
import flareHeader from 'assets/img/flare-header.png';
import { cn } from 'lib/utils';

const LogoContainer = styled.div`
  padding: 0 !important;

  & .logo-normal {
    padding: 0 !important;

    & img {
      height: 75px;
    }
  }

  & .logo-mini {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;

    & img {
      margin: auto;
      max-height: 80px;
    }
  }
`;

var ps;

function Sidebar(props) {
  const { session } = props;

  const { name, email, image, permissions } = session || {};
  const location = useLocation();
  const [openAvatar, setOpenAvatar] = React.useState(false);
  const [collapseStates, setCollapseStates] = React.useState({});
  const sidebar = React.useRef();
  // this creates the intial state of this component based on the collapse routes
  // that it gets through props.routes
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop, key) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });

    return initialState;
  };
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.js - route /admin/regular-forms
  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (window.location.pathname.indexOf(routes[i].path) !== -1) {
        return true;
      }
    }
    return false;
  };
  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinks = (routes) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }

      if (prop.permission) {
        if (!permissions[prop.permission]) return null;
      }

      if (prop.permissions) {
        if (!prop.permissions.some((per) => permissions[per])) return null;
      }

      if (prop.collapse) {
        var st = {};
        st[prop['state']] = !collapseStates[prop.state];

        const permissionList = _.compact(prop.views.map((view) => view.permission));
        if (permissionList.length > 0 && !permissionList.some((v) => permissions[v])) return null;

        let navItem = (
          <>
            <span className="sidebar-mini-icon">{prop.mini}</span>
            <span className="sidebar-normal">{prop.name}</span>
          </>
        );

        if (prop.icon) {
          navItem = (
            <div className="tw-flex tw-items-center tw-gap-4">
              <span className="tw-inline-flex tw-items-center">{prop.icon}</span>
              <p className="tw-m-0">{prop.name}</p>
              <b className="caret tw-self-center" />
            </div>
          );
        } else if (prop.iconClassName) {
          navItem = (
            <>
              <i className={prop.iconClassName} />
              <p>
                {prop.name}
                <b className="caret tw-self-center" />
              </p>
            </>
          );
        }

        return (
          <li
            className={cn('hover:tw-text-white tw-select-none', { active: getCollapseInitialState(prop.views) })}
            key={key}
          >
            <a
              data-toggle="collapse"
              aria-expanded={collapseStates[prop.state]}
              onClick={(e) => {
                e.preventDefault();
                setCollapseStates(st);
              }}
            >
              {navItem}
            </a>
            <Collapse isOpen={collapseStates[prop.state]}>
              <ul className="nav hover:tw-text-white">{createLinks(prop.views)}</ul>
            </Collapse>
          </li>
        );
      }

      let navItem = (
        <>
          <span className="sidebar-mini-icon">{prop.mini}</span>
          <span className="sidebar-normal">{prop.name}</span>
        </>
      );

      if (prop.icon) {
        navItem = (
          <div className="tw-flex tw-items-center tw-gap-4">
            <span>{prop.icon}</span>
            <p className="tw-inline-block">{prop.name}</p>
          </div>
        );
      } else if (prop.iconClassName) {
        navItem = (
          <>
            <i className={prop.iconClassName} />
            <p>{prop.name}</p>
          </>
        );
      }

      return (
        <li className={activeRoute(prop.layout + prop.path)} key={key}>
          <NavLink to={prop.layout + prop.path} activeclassname="">
            {navItem}
          </NavLink>
        </li>
      );
    });
  };

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname.indexOf(routeName) > -1 ? 'active' : '';
  };

  React.useEffect(() => {
    // if you are using a Windows Machine, the scrollbars will have a Mac look
    if (navigator.platform.indexOf('Win') > -1) {
      ps = new PerfectScrollbar(sidebar.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    return function cleanup() {
      // we need to destroy the false scrollbar when we navigate
      // to a page that doesn't have this component rendered
      if (navigator.platform.indexOf('Win') > -1) {
        ps.destroy();
      }
    };
  }, []);

  React.useEffect(() => {
    setCollapseStates(getCollapseStates(props.routes));
  }, []);

  return (
    <div className="sidebar" data-color={props.bgColor} data-active-color={props.activeColor}>
      <LogoContainer className="logo">
        <a href="/" className="simple-text logo-mini">
          <img src={smallIcon} width="100%" alt="flare-logo" />
        </a>
        <a href="/" className="simple-text logo-normal">
          <img src={flareHeader} width="100%" alt="flare-logo" />
        </a>
      </LogoContainer>

      <div className="sidebar-wrapper" ref={sidebar}>
        {session && (
          <div className="tw-text-white tw-mt-3 tw-px-3 tw-text-center tw-cursor-default username">
            <span>{name}</span>
          </div>
        )}

        {session && <Nav>{createLinks(props.routes)}</Nav>}
      </div>
    </div>
  );
}

export default Sidebar;
